import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiFetch, ApiError } from '../../lib/api-client'
import { createSigningOrder, getSigningOrder, type SigningOrderResult, type SigningOrderStatus } from '../../lib/signing'
import type { WizardState, Lease, Renter, Company, ErhvervContract, LejerManuel } from './wizard/types'
import { INITIAL_WIZARD_STATE, toOere, validateWizard, isCommercialUsage } from './wizard/types'
import { PreWizard } from './wizard/PreWizard'
import { WizardShell } from './wizard/WizardShell'
import { reconcileRegime } from './wizard/regimeSync'
import { specialTermsFor } from './wizard/SectionVilkaar'
import { ErhvervWizardShell } from './wizard/ErhvervWizardShell'
import { AppLayout } from '../../components/layout/AppLayout'

type FlowState = 'PRE_PICK_LEASE' | 'PRE_PICK_TYPE' | 'WIZARD' | 'ERHVERV'

export function ContractWizardPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const draftContractId = searchParams.get('draft')
  const [flow, setFlow] = useState<FlowState>(draftContractId ? 'WIZARD' : 'PRE_PICK_LEASE')
  const [leases, setLeases] = useState<Lease[]>([])
  const [renters, setRenters] = useState<Renter[]>([])
  const [company, setCompany] = useState<Company | null>(null)
  const [loading, setLoading] = useState(true)
  const [wizState, setWizState] = useState<WizardState>(INITIAL_WIZARD_STATE)
  const [erhvervLease, setErhvervLease] = useState<Lease | null>(null)
  // A3 — the draft a commercial wizard resumes from (?draft=<id>). Held until
  // the leases list has loaded so the matching lease can be passed to the shell.
  const [erhvervDraft, setErhvervDraft] = useState<ErhvervContract | null>(null)
  const [currentSection, setCurrentSection] = useState(0)
  const [doneSections, setDoneSections] = useState<Set<number>>(new Set())
  const [draftId, setDraftId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Idura MitID signing (step F) for the residential wizard. Once the order is
  // created we surface the "sendt til underskrift" state in SectionSend with
  // per-party links + status (polled from GET /contracts/{id}/signing-order).
  const [sentResult, setSentResult] = useState<SigningOrderResult | null>(null)
  const [sentStatus, setSentStatus] = useState<SigningOrderStatus | null>(null)
  const [sentContractId, setSentContractId] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      apiFetch<Lease[]>('/api/v1/leases'),
      apiFetch<Renter[]>('/api/v1/renters'),
      apiFetch<Company[]>('/api/v1/companies'),
    ]).then(([ls, rs, cos]) => {
      setLeases(ls)
      setRenters(rs)
      setCompany(cos[0] ?? null)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  // Restore an existing draft opened via ?draft=<id>. Commercial drafts route
  // to the erhverv shell (which hydrates every field); residential drafts only
  // restore the step position (the A9/A10 shell re-reads its own fields).
  useEffect(() => {
    if (!draftContractId) return
    apiFetch<ErhvervContract>(`/api/v1/contracts/${draftContractId}`)
      .then(data => {
        if (!data) return
        setDraftId(draftContractId)
        if (data.contract_kind === 'commercial') {
          setErhvervDraft(data)
          setFlow('ERHVERV')
          // Fetch the lease detail so forbrug_availability (migr. 076) is the
          // real per-forbrugstype map for the Forbrug-step greying — the list
          // lease (matched in activeErhvervLease) carries it empty.
          if (data.lease_id) {
            apiFetch<Lease>(`/api/v1/leases/${data.lease_id}`)
              .then(detail => setErhvervLease(detail))
              .catch(() => { /* fall back to the list lease; availability all-false (safe) */ })
          }
        } else {
          setCurrentSection(data.last_step ?? 0)
        }
      })
      .catch(() => {
        // Draft not found or expired — start fresh
        setCurrentSection(0)
      })
  }, [draftContractId])

  // A3 — once a draft contract id is known (created or resumed), reflect it in
  // the URL so a reload re-opens the same draft instead of restarting at step 0.
  const publishDraftId = (id: string) => {
    setDraftId(id)
    if (searchParams.get('draft') !== id) {
      const next = new URLSearchParams(searchParams)
      next.set('draft', id)
      setSearchParams(next, { replace: true })
    }
  }

  const handleLeasePicked = (lease: Lease) => {
    // Erhverv lejemål route to the dedicated commercial flow — aftalefrihed,
    // no a conto / forbrug / moms, so a leaner form than the A9/A10 wizard.
    if (isCommercialUsage(lease.usage_type)) {
      // The list endpoint returns forbrug_availability empty ({}); the per-lease
      // GET derives the real per-forbrugstype map (migr. 076) the wizard's
      // Forbrug-step greying reads. Fetch it before handing the lease to the shell.
      setErhvervLease(lease)
      setFlow('ERHVERV')
      apiFetch<Lease>(`/api/v1/leases/${lease.id}`)
        .then(detail => setErhvervLease(detail))
        .catch(() => { /* keep the list lease; availability stays all-false (safe) */ })
      return
    }

    const prefilled: string[] = ['property_address', 'lease_display_type']
    if (lease.area_m2 != null) prefilled.push('area_m2')
    if (lease.room_count != null) prefilled.push('room_count')
    if (lease.property_city) prefilled.push('property_city')
    if (lease.floor != null) prefilled.push('floor')
    if (lease.energy_label) prefilled.push('energy_label')
    if (lease.unit_no) prefilled.push('unit_number')

    // Strip city/postal from address so it doesn't duplicate the By field
    const rawAddr = lease.property_address ?? ''
    const city = lease.property_city ?? ''
    let streetOnly = rawAddr
    if (city) {
      const cityIdx = rawAddr.indexOf(', ' + city)
      if (cityIdx !== -1) streetOnly = rawAddr.slice(0, cityIdx).trim()
    }
    const addressWithUnit = [streetOnly || rawAddr, lease.unit_no].filter(Boolean).join(', ')

    setWizState(prev => ({
      ...prev,
      lease_id: lease.id,
      property_address: addressWithUnit,
      property_city: lease.property_city ?? '',
      area_m2: lease.area_m2 != null ? String(lease.area_m2) : '',
      room_count: lease.room_count != null ? String(lease.room_count) : '',
      floor: lease.floor != null ? String(lease.floor) : '',
      energy_label: lease.energy_label ?? '',
      lease_display_type: lease.type === 'residential' ? 'lejlighed' : 'andet',
      beboerrepraesentation: lease.beboerrepraesentation,
      unit_number: lease.unit_no || prev.unit_number,
      prefilled_fields: prefilled,
    }))
    setFlow('PRE_PICK_TYPE')
  }

  // Annoncering → kontraktudkast: ?lease_id=<id> pre-picks the lease exactly as if
  // the user had chosen it in the list (once, only on a fresh flow, never with ?draft=).
  const preselectLeaseId = searchParams.get('lease_id')
  const preselectDone = useRef(false)
  useEffect(() => {
    if (preselectDone.current || !preselectLeaseId || draftContractId || loading) return
    if (flow !== 'PRE_PICK_LEASE') return
    const lease = leases.find(l => l.id === preselectLeaseId)
    preselectDone.current = true
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (lease) handleLeasePicked(lease)
  }, [preselectLeaseId, draftContractId, loading, flow, leases])

  const handleChange = (patch: Partial<WizardState>) => {
    setWizState(prev => {
      const next = { ...prev, ...patch }
      // Regime-klausuler afstemmes straks ved ændring af fri leje/grundlag/regime, så en
      // forældet auto-tilføjet §54-klausul aldrig bliver liggende, uanset hvilket trin der
      // er åbent. Landlords egne til-/fravalg røres ikke (se wizard/regimeSync.ts).
      if (!('free_rent' in patch || 'free_rent_reason' in patch || 'rent_regime' in patch)) return next
      const r = reconcileRegime(
        next.smartvilkaar_active ?? [],
        { freeRent: next.free_rent, reason: next.free_rent_reason, regime: next.rent_regime },
        next.regime_applied,
      )
      const prevIds = next.smartvilkaar_active ?? []
      const changed = r.ids.join() !== prevIds.join()
      // Regenerate §11 unless the landlord edited the text freely in textbox mode (clause
      // overrides are lifted into state, so they are kept). Free edits are kept but flagged for review,
      // so a stale auto-clause is never left in silently.
      const overrides = next.smart_overrides ?? {}
      const untouched = !next.special_terms_edited
      return {
        ...next,
        rent_regime: r.regime,
        regime_applied: r.applied,
        ...(changed
          ? {
              smartvilkaar_active: r.ids,
              ...(untouched
                ? { special_terms: specialTermsFor(r.ids, next.special_terms_clauses, overrides) }
                : { special_terms_review: true }),
            }
          : {}),
      }
    })
  }

  const saveDraft = async (state: WizardState, stepOverride?: number): Promise<string> => {
    const body = {
      lease_id: state.lease_id,
      type: 'a9',
      last_step: stepOverride !== undefined ? stepOverride : currentSection,
      monthly_rent_oere: toOere(state.monthly_rent_dkk) || 1,
      deposit_oere: toOere(state.deposit_dkk),
      prepaid_rent_oere: toOere(state.prepaid_rent_dkk),
      start_date: state.start_date || new Date().toISOString().split('T')[0],
      end_date: state.is_fixed_term && state.end_date ? state.end_date : null,
      lease_display_type: state.lease_display_type || null,
      is_sublease: state.is_sublease,
      commercial_area_m2: state.commercial_area_m2 ? parseFloat(state.commercial_area_m2) : null,
      payment_day: parseInt(state.payment_day || '1'),
      first_payment_date: state.first_payment_date || null,
      first_payment_oere: toOere(state.first_payment_dkk) || null,
      aconto_heating_oere: toOere(state.aconto_heating_dkk),
      aconto_water_oere: toOere(state.aconto_water_dkk),
      aconto_electricity_oere: toOere(state.aconto_electricity_dkk),
      aconto_cooling_oere: toOere(state.aconto_cooling_dkk),
      aconto_antenna_oere: toOere(state.aconto_antenna_dkk),
      aconto_internet_oere: toOere(state.aconto_internet_dkk),
      beboer_amount_oere: toOere(state.beboer_amount_dkk),
      extra_contributions: state.extra_contributions.map(c => ({ name: c.name, amount_oere: toOere(c.dkk) })),
      heating_provider: state.heating_provider || null,
      heating_type: state.heating_type || null,
      water_provider: state.water_provider || null,
      electricity_provider: state.electricity_provider || null,
      cooling_provider: state.cooling_provider || null,
      heating_billing_type: state.heating_billing_type ?? null,
      water_billing_type: state.water_billing_type ?? null,
      el_billing_type: state.el_billing_type ?? null,
      cooling_billing_type: state.cooling_billing_type ?? null,
      antenna_type: state.antenna_type ?? null,
      internet_type: state.internet_type ?? null,
      antenna_included: state.antenna_included,
      antenna_notes: state.antenna_notes || null,
      internet_included: state.internet_included,
      internet_provider: state.internet_provider || null,
      inspection_date: state.inspection_date || null,
      inspection_time: state.inspection_time || null,
      interior_maintenance: state.interior_maintenance,
      maintenance_notes: state.maintenance_notes || null,
      inventory_notes: state.inventory_notes || null,
      pets_allowed: state.pets_allowed,
      pets_notes: state.pets_notes || null,
      nettoprisindeks: state.nettoprisindeks,
      nettoprisindeks_base_year: state.nettoprisindeks_base_year ? parseInt(state.nettoprisindeks_base_year) : null,
      regulation_day: state.regulation_day || null,
      regulation_month: state.regulation_month || null,
      regulation_index_from: state.regulation_index_from || null,
      regulation_index_to: state.regulation_index_to || null,
      regulation_first_date: state.regulation_first_date || null,
      regulation_max_pct: state.regulation_max_pct || null,
      free_rent: state.free_rent,
      free_rent_reason: state.free_rent_reason || null,
      husorden: state.husorden || null,
      other_notes: state.other_notes || null,
      unit_number: state.unit_number || null,
      exterior_maintenance: state.exterior_maintenance,
      appliances: state.appliances,
      appliances_other: state.appliances_other,
      smoking_allowed: state.smoking_allowed,
      pets_option: state.pets_option || null,
      move_in_inspection: state.move_in_inspection,
      move_out_inspection: state.move_out_inspection,
      payment_method: state.payment_method || 'manuel',
      deposit_payment_date: state.deposit_payment_date || null,
      prepaid_payment_date: state.prepaid_payment_date || null,
      mitid_signing: state.mitid_signing,
      access_rights: state.access_rights || [],
      access_numbers: state.access_numbers || {},
      access_other: state.access_other || [],
      lejer_address: state.lejer_address || null,
    }

    if (!draftId) {
      const res = await apiFetch<{ id: string }>('/api/v1/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      publishDraftId(res.id)
      return res.id
    } else {
      await apiFetch(`/api/v1/contracts/${draftId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      return draftId
    }
  }

  const handleNext = async () => {
    setSaving(true)
    try {
      const nextSection = currentSection + 1
      await saveDraft(wizState, nextSection)
      setDoneSections(prev => new Set([...prev, currentSection]))
      setCurrentSection(nextSection)
    } catch (e) {
      console.error('Draft save failed', e)
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    setCurrentSection(prev => Math.max(0, prev - 1))
  }

  const handleSaveDraft = async () => {
    setSaving(true)
    try { await saveDraft(wizState) } finally { setSaving(false) }
  }

  const handleSaveDraftAndClose = async () => {
    setSaving(true)
    try {
      await saveDraft(wizState)
      navigate('/kontrakter')
    } finally { setSaving(false) }
  }

  async function handleDeleteDraft() {
    if (!draftId) { navigate('/kontrakter'); return }
    if (!window.confirm('Slet kladde? Dette kan ikke fortrydes.')) return
    try {
      await apiFetch(`/api/v1/contracts/${draftId}`, { method: 'DELETE' })
    } catch {
      alert('Kunne ikke slette kladden.')
      return
    }
    navigate('/kontrakter')
  }

  // Persist a manually-typed tenant as a real renter (dedup on email) so the
  // contract gets a real party — without this, manuel-mode left the PDF lejer
  // blank and the tenant had to be re-typed for every future contract.
  async function createOrFindRenter(lm: LejerManuel): Promise<string | null> {
    const cprDigits = (lm.cpr || '').replace(/\D/g, '')
    const body = {
      name: `${lm.firstname} ${lm.lastname}`.trim(),
      email: lm.email.trim(),
      phone: lm.phone || null,
      cpr: cprDigits.length === 10 ? cprDigits : undefined,
      status: 'active',
      manual_address: !!(lm.address || lm.postnr || lm.by),
      address_street: [lm.address, lm.address2].filter(Boolean).join(', ') || null,
      address_postnr: lm.postnr || null,
      address_by: lm.by || null,
      address_land: lm.land || null,
      bank_reg_nr: lm.reg_nr || null,
      bank_account_nr: lm.konto_nr || null,
    }
    try {
      const created = await apiFetch<{ id: string }>('/api/v1/renters', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      return created.id
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const all = await apiFetch<Array<{ id: string; email: string }>>('/api/v1/renters')
        return all.find(r => r.email.toLowerCase() === lm.email.trim().toLowerCase())?.id ?? null
      }
      throw e
    }
  }

  const handleSend = async () => {
    const errors = validateWizard(wizState)
    if (errors.length > 0) {
      // Never fail silently: the Send button is enabled once a tenant is chosen,
      // so tell the user which required fields are still missing.
      setSendError(`Udfyld de manglende felter før afsendelse: ${errors.map(e => `${e.label} (${e.stepName})`).join(', ')}.`)
      return
    }
    setSaving(true)
    setSendError(null)
    try {
      const id = await saveDraft(wizState)
      // Resolve tenant parties: 'select' uses the chosen renter; 'manuel'
      // persists each typed-in tenant as a renter first (no double-entry).
      const renterIds: string[] = []
      if (wizState.lejer_mode === 'select' && wizState.renter_id) {
        renterIds.push(wizState.renter_id)
      } else if (wizState.lejer_mode === 'manuel') {
        for (const lm of wizState.lejere_manuel) {
          if (!lm.firstname?.trim() || !lm.email?.trim()) continue
          const rid = await createOrFindRenter(lm)
          if (rid) renterIds.push(rid)
        }
      }
      for (let i = 0; i < renterIds.length; i++) {
        await apiFetch(`/api/v1/contracts/${id}/parties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ renter_id: renterIds[i], role: i === 0 ? 'primary_tenant' : 'co_tenant' }),
        })
      }
      const specialTerms = wizState.special_terms_clauses.filter(c => c.trim()).join('\n\n') || wizState.special_terms
      if (specialTerms) {
        await apiFetch(`/api/v1/contracts/${id}/clauses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paragraph_number: '§11', content: specialTerms, sort_order: 0 }),
        })
      }
      // Idura MitID signing (step F): create the signature order — renders the
      // PDF, one signatory per party (landlord + tenants), emails each their
      // personal MitID link. Show the sent-state in SectionSend on success.
      try {
        const result = await createSigningOrder(id)
        setSentResult(result)
        setSentStatus(null)
        setSentContractId(id)
      } catch (e) {
        // Idura unavailable (route missing / older backend) → fall back to the
        // legacy send-for-signing stub so the existing flow keeps working.
        if (e instanceof ApiError && e.status === 404) {
          await apiFetch(`/api/v1/contracts/${id}/send-for-signing`, { method: 'POST' })
          navigate(`/kontrakter/${id}`)
          return
        }
        setSendError(e instanceof ApiError ? e.message : 'Kunne ikke sende til underskrift.')
      }
    } catch (e) {
      console.error('Send failed', e)
      setSendError(e instanceof ApiError ? e.message : 'Kunne ikke sende til underskrift.')
    } finally {
      setSaving(false)
    }
  }

  // Step F — poll the residential signing order status while the sent-state UI
  // is open, so each party's afventer/underskrevet flips live. Stops on complete.
  useEffect(() => {
    if (!sentResult || !sentContractId) return
    let cancelled = false
    const poll = async () => {
      const s = await getSigningOrder(sentContractId)
      if (!cancelled && s) setSentStatus(s)
      return s
    }
    poll()
    const t = setInterval(async () => {
      const s = await poll()
      if (s && (s.signing_status === 'completed' || s.signed)) clearInterval(t)
    }, 5000)
    return () => { cancelled = true; clearInterval(t) }
  }, [sentResult, sentContractId])

  if (flow === 'PRE_PICK_LEASE' || flow === 'PRE_PICK_TYPE') {
    return (
      <AppLayout pageTitle="Ny kontrakt">
        <PreWizard
          screen={flow === 'PRE_PICK_LEASE' ? 'pick_lease' : 'pick_type'}
          leases={leases}
          loading={loading}
          onLeasePicked={handleLeasePicked}
          onNewTenancy={() => setFlow('WIZARD')}
          onNavigate={navigate}
        />
      </AppLayout>
    )
  }

  // On resume the lease comes from the loaded list (matched by the draft's
  // lease_id); on a fresh start it's the lease the user just picked.
  const activeErhvervLease = erhvervLease ?? (erhvervDraft ? leases.find(l => l.id === erhvervDraft.lease_id) ?? null : null)
  if (flow === 'ERHVERV') {
    // Resuming a draft: wait until the leases list has loaded so the matching
    // lease (and renters/company) can be handed to the shell.
    if (!activeErhvervLease) {
      return <AppLayout noHeader fullHeight><div style={{ padding: 40, color: '#6b7280' }}>{loading ? 'Indlæser kladde…' : 'Kladden kunne ikke findes.'}</div></AppLayout>
    }
    return (
      <AppLayout noHeader fullHeight>
        <ErhvervWizardShell
          lease={activeErhvervLease}
          renters={renters}
          company={company}
          initialContract={erhvervDraft}
          initialStep={erhvervDraft?.last_step ?? 0}
          onDraftCreated={publishDraftId}
          onCancel={() => navigate('/kontrakter')}
          onDone={(id) => navigate(`/kontrakter/${id}`)}
        />
      </AppLayout>
    )
  }

  return (
    <AppLayout noHeader fullHeight>
      <WizardShell
        state={wizState}
        onChange={handleChange}
        currentSection={currentSection}
        doneSections={doneSections}
        company={company}
        renters={renters}
        saving={saving}
        draftId={draftId}
        saveDraft={saveDraft}
        onSectionChange={setCurrentSection}
        onNext={handleNext}
        onBack={handleBack}
        onSaveDraft={handleSaveDraft}
        onSaveDraftAndClose={handleSaveDraftAndClose}
        onDeleteDraft={handleDeleteDraft}
        onSend={handleSend}
        onCompanyUpdate={(patch) => setCompany(prev => prev ? { ...prev, ...patch } : prev)}
        signingResult={sentResult}
        signingStatus={sentStatus}
        sendError={sendError}
        onSigningDone={() => sentContractId && navigate(`/kontrakter/${sentContractId}`)}
      />
    </AppLayout>
  )
}
